export const COOKIE_CONSENT_KEY = "minga_cookie_consent_v1";
export const COOKIE_CONSENT_VERSION = "2026-08-07";
export const COOKIE_CONSENT_DAYS = 180;

const POSTHOG_KEY =
  process.env.REACT_APP_POSTHOG_KEY ||
  "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";
const POSTHOG_HOST =
  process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

function nowIso() {
  return new Date().toISOString();
}

function expiresIso() {
  return new Date(
    Date.now() + COOKIE_CONSENT_DAYS * 24 * 60 * 60 * 1000,
  ).toISOString();
}

export function readCookieConsent() {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;

    const consent = JSON.parse(raw);
    if (!consent || consent.version !== COOKIE_CONSENT_VERSION) {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      return null;
    }

    if (!consent.expiresAt || new Date(consent.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      return null;
    }

    return consent;
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent() {
  return readCookieConsent()?.analytics === true;
}

export function saveCookieConsent({ analytics }) {
  const consent = {
    version: COOKIE_CONSENT_VERSION,
    necessary: true,
    analytics: analytics === true,
    savedAt: nowIso(),
    expiresAt: expiresIso(),
  };

  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
  } catch {
    // If storage is unavailable, the current in-memory choice still applies.
  }

  if (consent.analytics) enableAnalytics();
  else disableAnalytics();

  window.dispatchEvent(
    new CustomEvent("minga:cookie-consent-changed", { detail: consent }),
  );

  return consent;
}

function deleteCookie(name, domain) {
  const domainPart = domain ? `; domain=${domain}` : "";
  document.cookie = `${name}=; Max-Age=0; path=/${domainPart}; SameSite=Lax`;
}

function clearLegacyPostHogPersistence() {
  try {
    const cookieNames = document.cookie
      .split(";")
      .map((entry) => entry.split("=")[0]?.trim())
      .filter(Boolean);

    const host = window.location.hostname;
    const domainCandidates = [
      "",
      host,
      `.${host}`,
      ".mingaarchitecture.com",
    ];

    cookieNames
      .filter((name) => /^ph_.*_posthog$/i.test(name))
      .forEach((name) => {
        domainCandidates.forEach((domain) => deleteCookie(name, domain));
      });
  } catch {
    // Ignore cookie cleanup failures.
  }

  try {
    Object.keys(localStorage)
      .filter((key) => /^ph_.*_posthog$/i.test(key))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore storage cleanup failures.
  }
}

export function disableAnalytics() {
  window.__MINGA_ANALYTICS_DISABLED__ = true;

  try {
    window.posthog?.stopSessionRecording?.();
    window.posthog?.opt_out_capturing?.();
  } catch {
    // PostHog may not be loaded, which is the expected state before consent.
  }

  clearLegacyPostHogPersistence();
}

function installPostHogStub() {
  const t = document;
  const e = window.posthog || [];

  if (e.__SV) return e;

  window.posthog = e;
  e._i = [];

  e.init = function init(token, config, name) {
    function stub(target, method) {
      const parts = method.split(".");
      if (parts.length === 2) {
        target = target[parts[0]];
        method = parts[1];
      }
      target[method] = function queuedMethod() {
        target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
      };
    }

    const script = t.createElement("script");
    script.type = "text/javascript";
    script.crossOrigin = "anonymous";
    script.async = true;
    script.src =
      config.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") +
      "/static/array.js";

    const firstScript = t.getElementsByTagName("script")[0];
    firstScript.parentNode.insertBefore(script, firstScript);

    let instance = e;
    if (name !== undefined) instance = e[name] = [];
    else name = "posthog";

    instance.people = instance.people || [];
    instance.toString = function toString(detail) {
      let value = "posthog";
      if (name !== "posthog") value += `.${name}`;
      if (!detail) value += " (stub)";
      return value;
    };
    instance.people.toString = function peopleToString() {
      return `${instance.toString(1)}.people (stub)`;
    };

    const methods =
      "init capture register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset startSessionRecording stopSessionRecording identify alias set_config get_distinct_id people.set people.set_once people.unset people.increment people.append people.remove people.group people.delete_user people.toString".split(
        " ",
      );

    methods.forEach((method) => stub(instance, method));
    e._i.push([token, config, name]);
  };

  e.__SV = 1;
  return e;
}

export function enableAnalytics() {
  window.__MINGA_ANALYTICS_DISABLED__ = false;

  if (!POSTHOG_KEY) return;

  try {
    if (window.posthog?.capture && window.posthog?.opt_in_capturing) {
      window.posthog.opt_in_capturing();
      window.posthog.stopSessionRecording?.();
      return;
    }

    const posthog = installPostHogStub();
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: "identified_only",
      disable_session_recording: true,
      capture_pageview: true,
      capture_pageleave: true,
    });
  } catch {
    // Analytics must never break the public site.
  }
}

export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent("minga:open-cookie-settings"));
}
